/**
 * Básculas (Req. 14; tarea 15): cada una con su nombre y su unidad, para que
 * al elegirla el peso se capture en esa unidad. Una báscula con pesajes se
 * archiva en lugar de borrarse, así sus pesajes siguen contando (Req. 14.5).
 */

import { db } from './schema';
import { enEscritura } from './tx';
import type { Scale } from './types';
import { ahoraIso } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import { normalizar } from '@/domain/busqueda';
import { esUnidadPeso, type UnidadPeso } from '@/domain/units';

export const NOMBRE_BASCULA_MAX = 40;

/** Todas las básculas en su orden, las archivadas también. LECTURA PURA. */
export async function leerBasculas(): Promise<Scale[]> {
  const basculas = await db.scales.toArray();
  return basculas.sort((a, b) => a.orden - b.orden || a.creadoEn.localeCompare(b.creadoEn));
}

export interface BasculaConPesajes {
  bascula: Scale;
  pesajes: number;
}

/** Cada báscula con cuántos pesajes tiene, para saber si se borra o se archiva. LECTURA PURA. */
export async function leerBasculasConPesajes(): Promise<BasculaConPesajes[]> {
  const [basculas, pesajes] = await Promise.all([leerBasculas(), db.weighIns.toArray()]);
  return basculas.map((bascula) => ({ bascula, pesajes: pesajes.filter((p) => p.scaleId === bascula.id).length }));
}

export interface DatosBascula {
  nombre: string;
  unidad: UnidadPeso;
}

function validarBascula(datos: DatosBascula, otras: readonly Scale[]): { nombre: string; unidad: UnidadPeso } {
  const nombre = datos.nombre.trim().replace(/\s+/g, ' ');
  if (nombre === '') throw new ErrorValidacion('Escribe el nombre de la báscula, por ejemplo "Casa".', 'nombre');
  if (nombre.length > NOMBRE_BASCULA_MAX) {
    throw new ErrorValidacion(`El nombre puede tener hasta ${NOMBRE_BASCULA_MAX} caracteres.`, 'nombre');
  }
  if (!esUnidadPeso(datos.unidad)) throw new ErrorValidacion('Elige kg o lb.', 'unidad');
  const igual = otras.find((b) => normalizar(b.nombre) === normalizar(nombre));
  if (igual !== undefined) {
    throw new ErrorValidacion(
      igual.archivada
        ? `Ya tienes una báscula archivada que se llama "${igual.nombre}": desarchívala en lugar de crear otra.`
        : `Ya tienes una báscula que se llama "${igual.nombre}".`,
      'nombre',
    );
  }
  return { nombre, unidad: datos.unidad };
}

/**
 * Crea una báscula. El id lo crea la pantalla al abrirse: tocar dos veces
 * "Guardar" no la duplica (Req. 1.9). ESCRIBE.
 */
export async function crearBascula(id: string, datos: DatosBascula): Promise<Scale> {
  return enEscritura([db.scales], async () => {
    const existente = await db.scales.get(id);
    if (existente !== undefined) return existente;
    const todas = await db.scales.toArray();
    const { nombre, unidad } = validarBascula(datos, todas);
    const ahora = ahoraIso();
    const bascula: Scale = {
      id,
      nombre,
      unidad,
      archivada: false,
      orden: todas.reduce((max, b) => Math.max(max, b.orden + 1), 0),
      creadoEn: ahora,
      actualizadoEn: ahora,
    };
    await db.scales.add(bascula);
    return bascula;
  });
}

/**
 * Cambia el nombre o la unidad. Los pesajes ya guardados no cambian: cada uno
 * conserva su peso en kg y lo que se escribió (Req. 5.10). ESCRIBE.
 */
export async function editarBascula(id: string, datos: DatosBascula): Promise<void> {
  await enEscritura([db.scales], async () => {
    const bascula = await db.scales.get(id);
    if (bascula === undefined) throw new ErrorValidacion('Esa báscula ya no existe.');
    const otras = (await db.scales.toArray()).filter((b) => b.id !== id);
    const { nombre, unidad } = validarBascula(datos, otras);
    await db.scales.update(id, { nombre, unidad, actualizadoEn: ahoraIso() });
  });
}

/**
 * Archiva o desarchiva (Req. 14.5). Una archivada no se ofrece al pesarse,
 * pero sus pesajes siguen en la lista y en la tendencia. Si era la única que
 * contaba para la tendencia, vuelven a contar todas: con ella archivada la
 * tendencia ya no tendría pesajes nuevos (la pantalla lo dice antes). ESCRIBE.
 */
export async function archivarBascula(id: string, archivada: boolean): Promise<void> {
  await enEscritura([db.scales, db.settings], async () => {
    const bascula = await db.scales.get(id);
    if (bascula === undefined) throw new ErrorValidacion('Esa báscula ya no existe.');
    await db.scales.update(id, { archivada, actualizadoEn: ahoraIso() });
    if (!archivada) return;
    const ajustes = await db.settings.get(1);
    if (ajustes === undefined) return;
    const cambios: { basculaTendenciaId?: null; ultimaBasculaId?: null } = {};
    if (ajustes.basculaTendenciaId === id) cambios.basculaTendenciaId = null;
    // Ya no se propone al pesarse (Req. 14.3)
    if (ajustes.ultimaBasculaId === id) cambios.ultimaBasculaId = null;
    if (Object.keys(cambios).length > 0) await db.settings.update(1, cambios);
  });
}

/**
 * Borra una báscula sin pesajes. Con pesajes no se borra: se archiva (Req.
 * 14.5). Si contaba para la tendencia, vuelven a contar todas. ESCRIBE.
 */
export async function borrarBascula(id: string): Promise<void> {
  await enEscritura([db.scales, db.weighIns, db.settings], async () => {
    const bascula = await db.scales.get(id);
    if (bascula === undefined) return;
    const pesajes = await db.weighIns.where('scaleId').equals(id).count();
    if (pesajes > 0) {
      throw new ErrorValidacion(
        `"${bascula.nombre}" tiene ${pesajes === 1 ? '1 pesaje' : `${pesajes} pesajes`}: archívala para conservarlos.`,
      );
    }
    await db.scales.delete(id);
    const ajustes = await db.settings.get(1);
    if (ajustes === undefined) return;
    const cambios: { basculaTendenciaId?: null; ultimaBasculaId?: null } = {};
    if (ajustes.basculaTendenciaId === id) cambios.basculaTendenciaId = null;
    if (ajustes.ultimaBasculaId === id) cambios.ultimaBasculaId = null;
    if (Object.keys(cambios).length > 0) await db.settings.update(1, cambios);
  });
}

/** Qué básculas cuentan para la tendencia: todas (null) o una (Req. 14.4). ESCRIBE. */
export async function elegirBasculaDeTendencia(id: string | null): Promise<void> {
  await enEscritura([db.scales, db.settings], async () => {
    if (id !== null) {
      const bascula = await db.scales.get(id);
      if (bascula === undefined) throw new ErrorValidacion('Esa báscula ya no existe.');
      if (bascula.archivada) throw new ErrorValidacion(`"${bascula.nombre}" está archivada: desarchívala primero.`);
    }
    await db.settings.update(1, { basculaTendenciaId: id });
  });
}
